import { isEmpty, mapValues, orderBy } from 'lodash';
import moment from 'moment';

import type { Event, EventUnitInfo, UnitEventDetails } from '@appTypes/esb';
import {
  ProgramId,
  programByProgramId,
  timeFormat,
  unitTypes,
} from '@modules/shared/constants';

import { sortTags } from './constants';
import type { CalendarEvent, PersonsList, PersonsRSVPList } from './duck/types';

export const getTimeOptions = () => {
  const beginning = moment('12:00 am', timeFormat);
  const ending = moment('11:45 pm', timeFormat);
  let current = beginning;
  const options = [];

  while (current.isSameOrBefore(ending)) {
    options.push(current.format(timeFormat));
    current = current.add(15, 'minute');
  }
  return options;
};

export enum Demographics {
  YOUTHS = 'YTH',
  PARENTS = 'PNT',
  LEADERS = 'LED',
  YOUTHS_N_PARENTS = 'YP',
  YOUTHS_N_LEADERS = 'YL',
  PARENTS_N_LEADERS = 'PL',
  ALL = 'ALL',
  NA = 'NA',
}

export enum Recipients {
  LEADER = 'leader',
  ALL = 'all',
}

export const getLookUpDemographicValue = ({
  leader,
  youth,
  parent,
}: {
  leader: boolean;
  youth: boolean;
  parent: boolean;
}) => {
  if (youth && parent && leader) return Demographics.ALL;
  if (youth && leader) return Demographics.YOUTHS_N_LEADERS;
  if (youth && parent) return Demographics.YOUTHS_N_PARENTS;
  if (parent && leader) return Demographics.PARENTS_N_LEADERS;
  if (parent) return Demographics.PARENTS;
  if (leader) return Demographics.LEADERS;
  if (youth) return Demographics.YOUTHS;

  return Demographics.NA;
};

export const getFormDemographicValues = (demo: string) => {
  if (demo === Demographics.NA)
    return {
      naAudience: true,
    };
  if (demo === Demographics.ALL)
    return {
      leadersAudience: true,
      youthAudience: true,
      parentsAudience: true,
    };
  if (demo === Demographics.YOUTHS_N_LEADERS)
    return { leadersAudience: true, youthAudience: true };
  if (demo === Demographics.YOUTHS_N_PARENTS)
    return { youthAudience: true, parentsAudience: true };
  if (demo === Demographics.PARENTS_N_LEADERS)
    return { leadersAudience: true, parentsAudience: true };
  if (demo === Demographics.PARENTS)
    return {
      parentsAudience: true,
    };
  if (demo === Demographics.LEADERS)
    return {
      leadersAudience: true,
    };
  if (demo === Demographics.YOUTHS)
    return {
      youthAudience: true,
    };
  return {};
};

enum EventTagAttr {
  PlansReviewedParents = 'plansReviewedParents',
  GrowthPlan = 'growthPlan',
  BudgetIncludesScouts = 'budgetIncludesScouts',
  OutdoorActivity = 'outdoorActivity',
  ParentalInvolvement = 'parentalInvolvement',
  MyScoutingTools = 'myScoutingTools',
  PatrolTraining = 'patrolTraining',
  ServiceProject = 'serviceProject',
  GroupFitness = 'groupFitness',
  BudgetScoutsParticipate = 'budgetScoutsParticipate',
  ParentOrientation = 'parentOrientation',
  ServiceProjectBenefit = 'serviceProjectBenefit',
}

// Confused? read SBL-5786
export enum EventTag {
  'Sub-Unit Meeting' = EventTagAttr.ServiceProject,
  'Unit Meeting' = EventTagAttr.MyScoutingTools,
  'Youth Training' = EventTagAttr.ServiceProjectBenefit,
  'Adult Training' = EventTagAttr.ParentOrientation,
  'Recruiting' = EventTagAttr.BudgetScoutsParticipate,
  'Camping' = EventTagAttr.OutdoorActivity,
  'Annual Planning' = EventTagAttr.GrowthPlan,
  'Youth Leaders Meeting' = EventTagAttr.PatrolTraining,
  'Recognition Event' = EventTagAttr.BudgetIncludesScouts,
  'Other' = EventTagAttr.PlansReviewedParents,
  'Committee/Parent Meeting' = EventTagAttr.ParentalInvolvement,
  'Long Term Outdoor Event' = EventTagAttr.GroupFitness,
}

export const getTags = (eventTypesName: string[], eventData: Event) => {
  const resultTags: Record<string, boolean> = {};

  if (eventTypesName?.length) {
    eventTypesName.forEach(attributeKey => (resultTags[attributeKey] = true));
  }

  // New event
  if (isEmpty(eventData)) return resultTags;

  // Editing: Remove event types deselected
  Object.values(EventTagAttr).forEach(attr => {
    const eventHasAttribute = !!eventData[attr];
    const isAttrSelected = eventTypesName.includes(attr);

    if (eventHasAttribute && !isAttrSelected) resultTags[attr] = false;
  });

  return resultTags;
};

export const getFormEventTagsValues = (eventData: Event | UnitEventDetails) => {
  const result: string[] = [];
  Object.values(EventTagAttr).forEach(attr => {
    const eventHasAttribute = !!eventData[attr];
    if (eventHasAttribute) result.push(attr);
  });
  return { eventTypeTag: result };
};

export const getEventTagsLabels = (
  eventData: Event | CalendarEvent['resource'],
) => {
  if (!eventData || isEmpty(eventData)) return '';

  const resultTags: string[] = [];

  Object.keys(EventTag).forEach(key => {
    const attribute = EventTag[key as keyof typeof EventTag];
    if (eventData[attribute]) resultTags.push(key);
  });

  return resultTags.length > 1
    ? resultTags.toString().replaceAll(',', ', ')
    : resultTags.toString();
};

export const sortPersonsBy = (data: PersonsList, sortByValue: string) => {
  const firstSort =
    sortByValue === sortTags.FIRST_NAME
      ? sortTags.NICKNAME
      : sortTags.LAST_NAME;
  const secondSort =
    sortByValue === sortTags.FIRST_NAME
      ? sortTags.LAST_NAME
      : sortTags.NICKNAME;

  const sortedData = mapValues(data, persons => {
    const updatedPersons = persons.map(p => ({
      ...p,
      nickName: p.nickName ?? p.firstName,
    }));

    return orderBy(updatedPersons, [firstSort, secondSort], ['asc', 'asc']);
  });
  return sortedData;
};

export const sortPersonsByRSVP = (
  data: PersonsRSVPList,
  sortByValue: string,
) => {
  const firstSort =
    sortByValue === sortTags.FIRST_NAME
      ? sortTags.NICKNAME
      : sortTags.LAST_NAME;
  const secondSort =
    sortByValue === sortTags.FIRST_NAME
      ? sortTags.LAST_NAME
      : sortTags.NICKNAME;

  const sortedData = mapValues(data, rsvp =>
    mapValues(rsvp, persons => {
      const updatedPersons = persons.map(p => ({
        ...p,
        nickName: p.nickName ?? p.firstName,
      }));
      return orderBy(updatedPersons, [firstSort, secondSort], ['asc', 'asc']);
    }),
  );

  return sortedData;
};

const unitTypePattern = Object.values(unitTypes).join('|');

const simplifyUnitName = (text: string): string => {
  const regex = new RegExp(`\\b(${unitTypePattern})\\s*0*(\\d+)\\b`, 'i');

  const match = text.match(regex);

  if (!match) {
    return text;
  }

  const [, unitType, unitNumber] = match;

  return `${unitType} ${Number(unitNumber)}`;
};

/**
 * This is to format unit that comes in the event data and the log in user doesn't belong to said unit
 */
export const formateventUnitToDisplayInForm = (unit: EventUnitInfo) => {
  const id = unit.denId || unit.patrolId || unit.unitId;
  const isSubUnit = !!(unit.denId || unit.patrolId);
  const subUnitName = unit.denId ? unit.denNumber : unit.patrolName;
  const simpleUnitName = simplifyUnitName(unit.unitFullName);

  const formattedEventUnit = {
    id,
    name: isSubUnit ? subUnitName : simpleUnitName,
    unitGender: isSubUnit ? undefined : unit.councilAcceptGender,
    isDen: unit.denId,
    organizationGuid: '',
    groupName: simpleUnitName,
    isSubUnit,
    program: programByProgramId[unit.unitTypeId as ProgramId],
    showDlEvents: null,
    unitId: isSubUnit ? unit.id : undefined,
    extra: isSubUnit
      ? {
          subUnitId: id,
          subUnitName,
          denTypeId: null,
          denType: unit.denType,
          isApproved: true,
          isForumEnabled: false,
          showDlEvents: null,
          unitId: unit.id,
          dateCreated: '',
        }
      : undefined,
  };
  return formattedEventUnit;
};
